import type {
  AmendBookingInput,
  AmendedBooking,
  ApplyDiscountInput,
  Booking,
  ChangeBookingStageInput,
  ChangeGarmentStateInput,
  CreateBookingInput,
  ExtendBookingInput,
  IssueBookingInput,
  IssuePreview,
  RecordFittingInput,
  RecordPaymentInput,
  ReturnBookingInput,
  SettleDepositInput,
} from './bookings';
import type { PaymentMethod } from '../generated/enums';
import { payInParts, type SplitPart } from '../cash/split';
import { apiFetch } from './client';

/**
 * The counter's writes against a booking — one definition for the website and the phone, so the two
 * cannot send the server different requests for the same handover.
 *
 * <p>
 * <strong>Issuing moves garments; it never takes money.</strong> Cash reaches the ledger by one path,
 * {@link recordBookingPayment} (`RecordPaymentAsync` on the server), and a second one inside the
 * issue would be a second thing to keep right. A counter shows what is owed and offers the payment
 * beside the handover.
 * </p>
 */

/** Takes a booking. Money is not part of it — the token is a payment of its own, afterwards. */
export function createBooking(input: CreateBookingInput) {
  return apiFetch<Booking>('/api/booking', { method: 'POST', body: input });
}

/** Sends garments out. An empty list means everything ready; a partial list is a partial issue. */
export function issueBooking(id: number, input: IssueBookingInput) {
  return apiFetch<Booking>(`/api/booking/${id}/issue`, { method: 'POST', body: input });
}

/**
 * What that hand-over's invoice would add, asked before it is made. A read: nothing is numbered or
 * saved. A 400 is the refusal the hand-over itself would meet (a design nothing prices for tax).
 */
export function previewIssue(id: number, input: IssueBookingInput) {
  return apiFetch<IssuePreview>(`/api/booking/${id}/issue/preview`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Books garments back in, and settles the deposit when the shop refunds at the counter — netting
 * it against what is still owed, as {@link settleBookingDeposit} does.
 */
export function returnBooking(id: number, input: ReturnBookingInput) {
  return apiFetch<Booking>(`/api/booking/${id}/return`, {
    method: 'POST',
    body: { applyToBalance: true, ...input },
  });
}

/** Records a money event. The amount is unsigned; the kind carries the direction. */
export function recordBookingPayment(id: number, input: RecordPaymentInput) {
  return apiFetch<Booking>(`/api/booking/${id}/payment`, { method: 'POST', body: input });
}

/**
 * One payment taken part by part (canvas "Money in, by account", 2026-11-01): a receipt per account,
 * each naming its own, through the split loop Pay a supplier uses (`cash/split.ts` `payInParts`) —
 * nothing saved and refused throws; a refusal part-way answers what was saved.
 */
export function recordBookingPaymentInParts(
  id: number,
  input: { kind: RecordPaymentInput['kind']; notes: string | null },
  parts: readonly SplitPart[],
) {
  return payInParts(parts, (part, amount) =>
    recordBookingPayment(id, {
      kind: input.kind,
      method: part.method,
      amount,
      reference: part.reference.trim() || null,
      notes: input.notes,
      cashAccountId: part.accountId,
    }),
  );
}

/**
 * Moves a booking by a decision — quote, hold, confirm, expire, cancel, recover. Built only through
 * `stageMoveInput` (`bookings/stageMoves.ts`), which is what puts the hours on a hold.
 */
export function changeBookingStage(id: number, input: ChangeBookingStageInput) {
  return apiFetch<Booking>(`/api/booking/${id}/stage`, { method: 'POST', body: input });
}

/**
 * Moves one garment along the fulfilment track — ready, at a fitting, to the tailor. Only the moves
 * `bookings/garmentMoves.ts` lists; hand-over and take-back have their own commands above.
 */
export function changeGarmentState(id: number, garmentId: number, input: ChangeGarmentStateInput) {
  return apiFetch<Booking>(`/api/booking/${id}/garment/${garmentId}/state`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Schedules, moves or completes one person's fitting, and — from the phone's fitting screen —
 * writes the changes noted there onto their garments (`bookings/fittingNotes.ts`). The appointment
 * is a statement: build the request through `fittingRequest`, which carries it through.
 */
export function recordFitting(id: number, wearerId: number, input: RecordFittingInput) {
  return apiFetch<Booking>(`/api/booking/${id}/wearer/${wearerId}/fitting`, {
    method: 'PUT',
    body: input,
  });
}

/** Gives, changes or removes the discount. Built by `discountRequest` (`bookings/discountForm.ts`). */
export function applyBookingDiscount(id: number, input: ApplyDiscountInput = {}) {
  return apiFetch<Booking>(`/api/booking/${id}/discount`, { method: 'POST', body: input });
}

/**
 * Closes the deposit: deductions applied, then what is still owed paid from what is left, and only
 * the rest refunded — never a `DepositRefund` payment, which moves money and leaves the deposit open.
 *
 * <p>
 * <strong>Netting is on unless a caller says otherwise</strong> (owner, 2026-10-14: "most of the
 * time the pending payment comes off the deposit"). The server's default is off, so an older
 * client keeps its old behaviour; this is the one place both clients ask for it.
 * </p>
 */
export function settleBookingDeposit(id: number, input: SettleDepositInput = {}) {
  return apiFetch<Booking>(`/api/booking/${id}/deposit/settle`, {
    method: 'POST',
    body: { applyToBalance: true, ...input },
  });
}

/**
 * After a settlement the deposit could not fully cover, takes the rest in the same press: what the
 * booking then says is owed, by the method the counter chose, into the account it chose (split
 * payments; null is the method's default). Nothing when nothing is owed or no method was given — the
 * booking as settled comes back either way.
 */
export async function collectAfterSettling(
  settled: Booking,
  method: PaymentMethod | null,
  cashAccountId: number | null = null,
): Promise<Booking> {
  if (method === null || method === 'Adjustment' || settled.balanceDue <= 0) {
    return settled;
  }

  return recordBookingPayment(settled.id, {
    kind: 'RentalBalance',
    method,
    amount: settled.balanceDue,
    cashAccountId,
  });
}

/**
 * Adds, drops and swaps garments as one atomic list — built by `amendmentRequest`
 * (`bookings/amendment.ts`). Answers with the booking and what moved.
 */
export function amendBooking(id: number, input: AmendBookingInput) {
  return apiFetch<AmendedBooking>(`/api/booking/${id}/amend`, { method: 'POST', body: input });
}

/**
 * Grants more days on a booking already out (spec Section 8, item 2.6).
 *
 * <p>
 * <strong>An extension is what a shop says yes to; a late fee is the same money when nobody
 * asked.</strong> It moves the booking's possession end, which is what makes the days it covers stop
 * counting as overdue — so a day is charged once, at the rate the card froze.
 * </p>
 *
 * <p>
 * It records what is owed and takes no money: the counter collects through the payment call like
 * every other taking. A garment a downstream booking wants refuses the whole call with a 409.
 * </p>
 */
export function extendBooking(id: number, input: ExtendBookingInput) {
  return apiFetch<Booking>(`/api/booking/${id}/extend`, { method: 'POST', body: input });
}
